// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// SecurityArea.tsx - Settings › Security. The left sub-nav entries LOAD PAGES (prototype secView / scGo):
// Platform · Users · User groups. (Audit log is hidden until its viewer ships; Support access is Phase 2.)
// Each page brings its own page head (title, description, its one primary action - §12):
//   Platform    -> Discard draft · Save all changes
//   Users       -> Invite user
//   User groups -> Discard · Save groups
// Pages the person has no access to are not offered (useAccess). Visited pages stay mounted (hidden) so an unsaved
// Platform or User-groups draft survives switching pages; the unsaved state is reported up to SettingsPage for the
// nav dots and the leave-guard.
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Ic, useConfirm } from '../../components/security/ui';
import { useAccess } from '../../lib/useAccess';
import GroupsPage from './GroupsPage';
import PlatformPage, { type SecPageState } from './PlatformPage';
import UsersPage from './UsersPage';
import '../../styles/security.css';

export type SecurityPageKey = 'platform' | 'users' | 'groups';
export const SECURITY_PAGES: { key: SecurityPageKey; label: string; area: string }[] = [
  { key: 'platform', label: 'Platform', area: 'sec_platform' },
  { key: 'users', label: 'Users', area: 'sec_users' },
  { key: 'groups', label: 'User groups', area: 'sec_roles' },
];
export interface SecurityState {
  dirty: Record<SecurityPageKey, boolean>;
  anyDirty: boolean;
  saving: boolean;
  saveAll: () => void;
  discardAll: () => void;
}

/** Which Security pages this person may open (unknown access -> all). */
export function useSecurityPages(): { key: SecurityPageKey; label: string }[] {
  const { canOpen } = useAccess();
  return SECURITY_PAGES.filter((p) => canOpen(p.area) || (p.key === 'groups' && canOpen('sec_groups')));
}

export default function SecurityArea({ page, onPage, renderHeader, orgName, onStateChange }: {
  page: SecurityPageKey;
  onPage: (p: SecurityPageKey) => void;
  /** wraps the page head in Settings' sticky header (search row above it) */
  renderHeader: (hero: ReactNode) => ReactNode;
  orgName: string;
  onStateChange?: (s: SecurityState) => void;
}) {
  const { hasAccess, ready } = useAccess();
  const pages = useSecurityPages();
  const current: SecurityPageKey = pages.some((p) => p.key === page) ? page : (pages[0]?.key ?? 'platform');
  const [visited, setVisited] = useState<Record<SecurityPageKey, boolean>>({ platform: false, users: false, groups: false });
  const [platformState, setPlatformState] = useState<SecPageState | null>(null);
  const [groupsState, setGroupsState] = useState<SecPageState | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [groupFilter, setGroupFilter] = useState<number | 'all'>('all');
  const [confirmEl, ask] = useConfirm();

  useEffect(() => {
    setVisited((v) => (v[current] ? v : { ...v, [current]: true }));
    (document.querySelector('.tenant-main') as HTMLElement | null)?.scrollTo({ top: 0 });
  }, [current]);
  useEffect(() => { if (current !== page && ready) onPage(current); }, [current, page, ready, onPage]);

  const onPlatform = useCallback((s: SecPageState) => setPlatformState(s), []);
  const onGroups = useCallback((s: SecPageState) => setGroupsState(s), []);

  const dirty = useMemo(() => ({ platform: !!platformState?.dirty, users: false, groups: !!groupsState?.dirty }), [platformState, groupsState]);
  const anyDirty = dirty.platform || dirty.groups;
  const saving = !!platformState?.saving || !!groupsState?.saving;
  useEffect(() => {
    onStateChange?.({
      dirty, anyDirty, saving,
      saveAll: () => { if (platformState?.dirty) platformState.saveAll(); if (groupsState?.dirty) groupsState.saveAll(); },
      discardAll: () => { platformState?.discardAll(); groupsState?.discardAll(); },
    });
  }, [dirty, anyDirty, saving, platformState, groupsState, onStateChange]);

  function confirmDiscard(st: SecPageState | null, what: string) {
    if (!st?.dirty) return;
    ask({ sev: 'warn', title: 'Discard your changes?', body: `Discarding throws away every change you have made to ${what} since your last save. This cannot be undone.`, label: 'Discard changes', danger: true, cancelLabel: 'Keep editing', onOk: () => st.discardAll() });
  }

  const head = (() => {
    if (current === 'users') {
      return {
        title: 'Users', desc: 'Everyone who can sign in, and the groups each person is in.',
        acts: hasAccess('sec_users', 'invite')
          ? <button type="button" className="sc-primary" onClick={() => setInviteOpen(true)}><Ic k="plus" /> Invite user</button>
          : null,
      };
    }
    if (current === 'groups') {
      const d = !!groupsState?.dirty, s = !!groupsState?.saving;
      return {
        title: 'User groups', desc: 'Pick one group to edit, or several to compare.',
        acts: <>
          <button type="button" className="settings-secondary" disabled={!d || s} onClick={() => confirmDiscard(groupsState, 'user groups')}>Discard</button>
          <button type="button" className="settings-primary" disabled={!d || s} onClick={() => groupsState?.saveAll()}>{s ? 'Saving…' : 'Save groups'}</button>
        </>,
      };
    }
    const d = !!platformState?.dirty, s = !!platformState?.saving;
    return {
      title: 'Platform', desc: `Sign-in rules and sessions for everyone in ${orgName}.`,
      acts: <>
        <button type="button" className="settings-secondary" disabled={!d || s} onClick={() => confirmDiscard(platformState, 'the sign-in policy')}>Discard draft</button>
        <button type="button" className="settings-primary" disabled={!d || s} onClick={() => platformState?.saveAll()}>{s ? 'Saving…' : 'Save all changes'}</button>
      </>,
    };
  })();

  const hero = (
    <header className="settings-hero">
      <div className="settings-hero-row">
        <h1>{head.title}</h1>
        <div className="settings-global-actions vs-sec">{head.acts}</div>
      </div>
      <p className="settings-hero-desc">{head.desc}</p>
    </header>
  );

  if (!pages.length) {
    return (
      <>
        {renderHeader(hero)}
        <div className="vs-sec sec-page"><div className="sc-empty"><b>Security isn’t part of your groups</b><span>Ask someone who manages users and groups.</span></div></div>
      </>
    );
  }

  return (
    <>
      {renderHeader(hero)}
      <div className="vs-sec">
        <div className="sc-mobile-pages">
          <select aria-label="Security page" value={current} onChange={(e) => onPage(e.target.value as SecurityPageKey)}>
            {pages.map((p) => <option key={p.key} value={p.key}>{p.label}{(p.key === 'platform' && dirty.platform) || (p.key === 'groups' && dirty.groups) ? ' •' : ''}</option>)}
          </select>
        </div>
      </div>
      {(visited.platform || current === 'platform') && pages.some((p) => p.key === 'platform') && (
        <div hidden={current !== 'platform'}><PlatformPage orgName={orgName} onStateChange={onPlatform} /></div>
      )}
      {(visited.users || current === 'users') && pages.some((p) => p.key === 'users') && (
        <div hidden={current !== 'users'}>
          <UsersPage inviteOpen={inviteOpen && current === 'users'} onInviteClose={() => setInviteOpen(false)} groupFilter={groupFilter} onGroupFilter={setGroupFilter} />
        </div>
      )}
      {(visited.groups || current === 'groups') && pages.some((p) => p.key === 'groups') && (
        <div hidden={current !== 'groups'}><GroupsPage onStateChange={onGroups} onOpenUsers={(gid) => { setGroupFilter(gid); onPage('users'); }} /></div>
      )}
      {confirmEl}
    </>
  );
}
